import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { type MenuItem } from '@/types/common';
import useAuth from '@/components/UserContext';
import getInitials from '@/utils/getInitials';
import IcChevronDown from '@/assets/icons/ChevronDown.svg?react';
import NavigationLink from '@/components/Navigation/NavigationLink';
import styles from './AccountChip.module.scss';

interface AccountChipProps {
  allMenuItems: MenuItem[];
}

const AccountChip = ({ allMenuItems }: AccountChipProps) => {
  const { t } = useTranslation(['common']);
  const { userData } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) {
      return;
    }
    const handleOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  const accountParent = allMenuItems.find(
    (item) => item.mobileSection === 'profile'
  );
  const accountSubs = accountParent?.submenuItems ?? [];
  const helpItems = allMenuItems.filter(
    (item) => item.mobileSection === 'help'
  );
  const logoutItem = allMenuItems.find((item) => item.action === 'logout');

  // The chip highlights when the current route matches any of its internal
  // destinations. External links (e.g. Support → swipesign.io) are skipped.
  const chipRoutes = [accountParent, ...accountSubs, ...helpItems]
    .map((item) => item?.href)
    .filter((href): href is string => !!href && href.startsWith('/'));
  const isActive = chipRoutes.some((route) =>
    location.pathname.startsWith(route)
  );

  const displayName = userData.name || userData.email || '';
  const initials = getInitials(displayName);

  const closePopover = () => setOpen(false);

  return (
    <div className={styles.area} ref={wrapperRef}>
      {open && (
        <div className={styles.pop} role="menu">
          {accountParent && (
            <NavigationLink
              href={accountParent.href}
              Icon={accountParent.icon}
              label={accountParent.label}
              onClick={closePopover}
              className={styles.popRow}
            />
          )}
          {accountSubs.map((sub) => (
            <NavigationLink
              key={sub.label + sub.href}
              href={sub.href}
              Icon={sub.icon}
              label={sub.label}
              refreshOnSameClick={sub.refreshOnSameClick}
              onClick={closePopover}
              className={classNames(styles.popRow, styles.popSubRow)}
            />
          ))}
          {helpItems.length > 0 && <div className={styles.popDivider} />}
          {helpItems.map((item) => (
            <NavigationLink
              key={item.label + item.href}
              href={item.href}
              Icon={item.icon}
              label={item.label}
              target={item.target}
              onClick={closePopover}
              className={styles.popRow}
            />
          ))}
          {logoutItem && (
            <>
              <div className={styles.popDivider} />
              <NavigationLink
                action={logoutItem.action}
                highlight={logoutItem.highlight}
                href={logoutItem.href}
                Icon={logoutItem.icon}
                label={logoutItem.label}
                onClick={closePopover}
                className={styles.popRow}
              />
            </>
          )}
        </div>
      )}
      <button
        type="button"
        className={classNames(styles.chip, {
          [styles.chipOn]: open,
          [styles.chipActive]: isActive && !open,
        })}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={t('common:menu.account')}
      >
        <span className={styles.avatar}>{initials}</span>
        <span className={styles.meta}>
          <span className={styles.name}>
            {userData.name || t('common:menu.account')}
          </span>
          <span className={styles.mail}>{userData.email}</span>
        </span>
        <span className={styles.chev}>
          <IcChevronDown />
        </span>
      </button>
    </div>
  );
};

export default AccountChip;
